import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Coins, Check, X as XIcon, History as HistIcon, AlertTriangle, Plus, Package, Minus, HelpCircle, Pencil, Trash2 } from "lucide-react";

const POLICY_LABEL = {
  NO_PARTS: "Sin repuesto",
  WITH_PARTS: "Con repuesto",
  PENDING: "Pendiente",
};
const POLICY_BADGE = {
  NO_PARTS: "bg-sky-100 text-sky-800",
  WITH_PARTS: "bg-emerald-100 text-emerald-800",
  PENDING: "bg-amber-100 text-amber-800",
};

export default function AdminCosts() {
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyFor, setHistoryFor] = useState(null); // service_id whose history is open
  const [opPct, setOpPct] = useState(35);
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [s, c, settings] = await Promise.all([
        api.get("/services", { params: { active: true } }),
        api.get("/categories"),
        api.get("/admin/settings").catch(() => ({ data: {} })),
      ]);
      setServices(s.data);
      setCategories(c.data);
      if (settings?.data?.workshop_operational_pct != null) {
        setOpPct(settings.data.workshop_operational_pct);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = services;
    if (categoryFilter) list = list.filter((s) => s.category_id === categoryFilter);
    const term = q.trim().toLowerCase();
    if (term) list = list.filter((s) => (s.name || "").toLowerCase().includes(term));
    return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [services, q, categoryFilter]);

  const missingCount = useMemo(() => services.filter((s) => s.cost == null).length, [services]);

  async function saveCost(svc, newCostRaw) {
    let cost = newCostRaw === "" ? null : Number(newCostRaw);
    if (cost !== null && (Number.isNaN(cost) || cost < 0)) {
      toast.error("Costo inválido");
      return;
    }
    try {
      const r = await api.patch(`/services/${svc.id}`, { cost });
      setServices((old) => old.map((s) => (s.id === svc.id ? r.data : s)));
      toast.success("Costo actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function savePolicy(svc, policy) {
    try {
      const body = { parts_policy: policy };
      // Al pasar a NO_PARTS forzar cost=0 por regla B
      if (policy === "NO_PARTS") body.cost = 0;
      const r = await api.patch(`/services/${svc.id}`, body);
      setServices((old) => old.map((s) => (s.id === svc.id ? r.data : s)));
      toast.success("Clasificación actualizada");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function saveName(svc, newNameRaw) {
    const newName = (newNameRaw || "").trim();
    if (!newName) {
      toast.error("El nombre no puede estar vacío");
      return false;
    }
    if (newName === svc.name) return true; // sin cambios
    try {
      const r = await api.patch(`/services/${svc.id}`, { name: newName });
      // Reemplazamos el servicio preservando el mismo id — no se duplica ni se recrea.
      setServices((old) => old.map((s) => (s.id === svc.id ? r.data : s)));
      toast.success("Nombre actualizado");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  }

  /**
   * Actualiza el precio de venta al público del servicio existente.
   * - Reutiliza PATCH /services/{id} con el mismo modelo ServiceUpdate.
   * - Preserva id y referencias; no duplica el catálogo.
   * - Rechaza valores no finitos o negativos.
   * - NO altera presupuestos ya aprobados (el backend respeta approved).
   * - NO recalcula production_snapshot (los snapshots quedan congelados).
   */
  async function savePrice(svc, newPriceRaw) {
    const raw = String(newPriceRaw ?? "").trim();
    if (raw === "") {
      toast.error("Ingresá el precio");
      return false;
    }
    const normalized = raw.replace(/\$/g, "").replace(/\s/g, "").replace(",", ".");
    const n = Number(normalized);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Precio inválido (número ≥ 0 requerido)");
      return false;
    }
    // El campo a actualizar depende del price_type del servicio existente.
    const pt = svc.price_type || "FIXED";
    const body = {};
    if (pt === "FIXED") body.price = n;
    else if (pt === "FROM") body.price_from = n;
    else if (pt === "RANGE") {
      // Rango: en edición rápida, actualizar price_min (base). price_max permanece.
      body.price_min = n;
    } else if (pt === "VARIABLE" || pt === "CONSULT") {
      // Estos tipos no persisten un precio fijo; para asignar precio base fijo,
      // el admin debe convertir el price_type desde "Servicios y Precios".
      toast.error(`Este servicio es ${pt}; editá el precio desde Servicios y Precios o cambiá su tipo.`);
      return false;
    }
    try {
      const r = await api.patch(`/services/${svc.id}`, body);
      setServices((old) => old.map((s) => (s.id === svc.id ? r.data : s)));
      toast.success(`Precio actualizado a $${n.toFixed(2)}`);
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    }
  }

  /**
   * Elimina (o archiva si tiene referencias) un servicio del catálogo.
   * - Reutiliza DELETE /services/{id}: el backend desactiva si hay items en órdenes
   *   existentes (preserva snapshots) y elimina en duro si no hay referencias.
   * - NO modifica órdenes ni presupuestos existentes.
   * - El servicio desactivado deja de ofrecerse para nuevas reparaciones
   *   (el listado filtra por active=true).
   */
  async function deleteService(svc) {
    const ok = window.confirm(
      `¿Eliminar la reparación "${svc.name}"?\n\n` +
      `• Si ya se usó en órdenes → se archivará (se oculta de nuevas selecciones, se preservan datos históricos).\n` +
      `• Si nunca se usó → se eliminará definitivamente.\n\n` +
      `Esta acción NO modifica órdenes ni presupuestos existentes.`,
    );
    if (!ok) return;
    try {
      const r = await api.delete(`/services/${svc.id}`);
      if (r.data?.deactivated) {
        toast.success(`"${svc.name}" archivado (tenía órdenes asociadas)`);
      } else {
        toast.success(`"${svc.name}" eliminado`);
      }
      // Quitar del listado local (listado filtra active=true, por lo que ambos casos salen).
      setServices((old) => old.filter((s) => s.id !== svc.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-4 p-4">
      <header className="flex flex-wrap items-baseline gap-3">
        <h1 className="font-heading font-extrabold text-3xl text-slate-900 flex items-center gap-2">
          <Coins size={28} className="text-amber-600" /> Costos de reparación
        </h1>
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Solo administrador</span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm("¿Buscar y eliminar servicios duplicados del catálogo?\n\nSe conserva una sola fila por servicio (la más completa) y se eliminan definitivamente las redundantes.")) return;
              try {
                const r = await api.post("/admin/services/dedupe");
                const { before, after, removed, duplicate_groups, renamed_canonicals } = r.data;
                if (removed === 0) {
                  toast.success(`Catálogo limpio: ${before} servicios, 0 duplicados detectados.`);
                } else {
                  toast.success(`Dedupe OK · antes ${before} → después ${after} · ${removed} eliminados · ${duplicate_groups} grupos · ${renamed_canonicals} renombrados`);
                }
                await load();
              } catch (e) {
                toast.error(formatApiError(e));
              }
            }}
            data-testid="dedupe-services-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-amber-100 text-amber-900 border border-amber-300 px-3 py-2 text-sm font-heading font-bold hover:bg-amber-200 transition-colors"
            title="Limpia servicios duplicados del catálogo"
          >
            <AlertTriangle size={14} /> Limpiar duplicados
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            data-testid="add-service-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-heading font-bold hover:bg-slate-700 transition-colors"
          >
            <Plus size={16} /> Agregar reparación
          </button>
        </div>
      </header>

      {missingCount > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-2" data-testid="missing-cost-banner">
          <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>{missingCount}</b> {missingCount === 1 ? "servicio activo" : "servicios activos"} sin costo configurado.
            Las reparaciones que los usen no calcularán ganancia hasta que asignes un costo.
          </div>
        </div>
      )}

      <div className="card-base p-4 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Buscar</label>
          <div className="relative">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              className="input-base !pl-[42px]"
              placeholder="Nombre del servicio"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="cost-search-input"
            />
          </div>
        </div>
        <div className="min-w-[200px]">
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Categoría</label>
          <select
            className="input-base"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="cost-category-filter"
          >
            <option value="">Todas</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-10">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-slate-500 py-10">Sin resultados.</div>
      ) : (
        <div className="card-base overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Servicio</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">Clasificación</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Precio venta</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Costo repuesto</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Operativo imput.</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 text-right">Base est.</th>
                  <th className="p-3 font-mono text-[10px] uppercase tracking-widest text-slate-500 sticky right-0 bg-slate-50 z-10"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <ServiceCostRow key={s.id} service={s} opPct={opPct} onSave={saveCost} onPolicyChange={savePolicy} onNameSave={saveName} onPriceSave={savePrice} onDelete={deleteService} onHistory={() => setHistoryFor(s.id)} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {historyFor && (
        <CostHistoryModal serviceId={historyFor} onClose={() => setHistoryFor(null)} />
      )}

      {showCreate && (
        <CreateServiceModal
          categories={categories}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); toast.success("Reparación creada"); }}
        />
      )}
    </div>
  );
}

function ServiceCostRow({ service, opPct, onSave, onPolicyChange, onNameSave, onPriceSave, onDelete, onHistory }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(service.cost != null ? String(service.cost) : "");
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(service.name || "");
  const [nameSaving, setNameSaving] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceValue, setPriceValue] = useState("");
  const [priceSaving, setPriceSaving] = useState(false);
  useEffect(() => { setValue(service.cost != null ? String(service.cost) : ""); }, [service.cost]);
  useEffect(() => { if (!editingName) setNameValue(service.name || ""); }, [service.name, editingName]);

  async function commitName() {
    setNameSaving(true);
    const ok = await onNameSave(service, nameValue);
    setNameSaving(false);
    if (ok) setEditingName(false);
  }

  async function commitPrice() {
    setPriceSaving(true);
    const ok = await onPriceSave(service, priceValue);
    setPriceSaving(false);
    if (ok) setEditingPrice(false);
  }

  const price = service.price ?? service.price_from ?? service.price_min ?? null;
  const cost = service.cost;
  // Clasificación efectiva: si no está seteada, se infiere para la UI pero sin tocar la DB.
  const policy = service.parts_policy || (cost != null && cost > 0 ? "WITH_PARTS" : null);
  const hasPolicy = !!service.parts_policy;

  // Operativo y base estimada según policy vigente + opPct.
  let operativo = null, baseEst = null;
  if (policy === "NO_PARTS" && price != null) {
    operativo = Math.ceil(Number(price) * Number(opPct) / 100);
    baseEst = Number(price) - operativo;
  } else if (policy === "WITH_PARTS" && price != null && cost != null) {
    operativo = 0;
    baseEst = Number(price) - Number(cost);
  }

  return (
    <tr className="group/row border-b border-slate-100 hover:bg-slate-50 align-middle" data-testid={`cost-row-${service.id}`}>
      <td className="p-3">
        {editingName ? (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid={`name-edit-${service.id}`}>
            <input
              type="text"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitName(); }
                if (e.key === "Escape") { e.preventDefault(); setEditingName(false); setNameValue(service.name || ""); }
              }}
              autoFocus
              disabled={nameSaving}
              className="flex-1 min-w-[180px] px-2 py-1 border border-slate-300 rounded font-semibold text-slate-900"
              data-testid={`name-input-${service.id}`}
              placeholder="Nombre del servicio"
            />
            <button
              onClick={commitName}
              disabled={nameSaving}
              className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              data-testid={`name-save-${service.id}`}
              title="Guardar"
            >
              <Check size={14} />
            </button>
            <button
              onClick={() => { setEditingName(false); setNameValue(service.name || ""); }}
              disabled={nameSaving}
              className="p-1.5 rounded bg-slate-200 text-slate-600 hover:bg-slate-300"
              data-testid={`name-cancel-${service.id}`}
              title="Cancelar"
            >
              <XIcon size={14} />
            </button>
          </div>
        ) : (
          <div className="group flex items-start gap-1.5">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">{service.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{service.price_type} · {service.id.slice(0, 8)}</div>
            </div>
            <button
              type="button"
              onClick={() => { setNameValue(service.name || ""); setEditingName(true); }}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1.5 rounded hover:bg-slate-200 text-slate-500"
              data-testid={`name-edit-btn-${service.id}`}
              title="Editar nombre"
            >
              <Pencil size={14} />
            </button>
          </div>
        )}
      </td>
      <td className="p-3">
        <select
          value={service.parts_policy || "PENDING"}
          onChange={(e) => onPolicyChange(service, e.target.value)}
          data-testid={`policy-select-${service.id}`}
          className={`text-[11px] font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${POLICY_BADGE[service.parts_policy || "PENDING"]}`}
        >
          <option value="PENDING">Pendiente</option>
          <option value="NO_PARTS">Sin repuesto</option>
          <option value="WITH_PARTS">Con repuesto</option>
        </select>
      </td>
      <td className="p-3 text-right font-mono text-slate-900">
        {editingPrice ? (
          <div className="flex items-center gap-1 justify-end" data-testid={`price-edit-${service.id}`}>
            <input
              type="number" step="0.01" min="0" value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitPrice(); }
                if (e.key === "Escape") { e.preventDefault(); setEditingPrice(false); }
              }}
              autoFocus disabled={priceSaving} placeholder="0.00"
              className="w-24 px-2 py-1 border border-slate-300 rounded font-mono text-right"
              data-testid={`price-input-${service.id}`}
            />
            <button onClick={commitPrice} disabled={priceSaving}
              className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              data-testid={`price-save-${service.id}`} title="Guardar precio">
              <Check size={14} />
            </button>
            <button onClick={() => { setEditingPrice(false); setPriceValue(""); }} disabled={priceSaving}
              className="p-1.5 rounded bg-slate-200 text-slate-600 hover:bg-slate-300"
              data-testid={`price-cancel-${service.id}`} title="Cancelar">
              <XIcon size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setPriceValue(price != null ? String(price) : ""); setEditingPrice(true); }}
            className="font-mono text-right px-2 py-1 rounded hover:bg-slate-100 transition-colors"
            data-testid={`price-value-${service.id}`}
            title="Editar precio de venta al público"
          >
            {price != null ? `$${Number(price).toFixed(2)}` : <span className="text-slate-400">—</span>}
          </button>
        )}
      </td>
      <td className="p-3 text-right">
        {policy === "NO_PARTS" ? (
          <span className="font-mono text-slate-500">$0</span>
        ) : policy === "WITH_PARTS" ? (
          editing ? (
            <div className="flex items-center gap-1 justify-end">
              <input
                type="number" step="0.01" min="0" value={value}
                onChange={(e) => setValue(e.target.value)} autoFocus placeholder="0.00"
                className="w-24 px-2 py-1 border border-slate-300 rounded font-mono text-right"
                data-testid={`cost-input-${service.id}`}
              />
              <button onClick={async () => { await onSave(service, value); setEditing(false); }}
                className="p-1.5 rounded bg-emerald-500 text-white hover:bg-emerald-600" data-testid={`cost-save-${service.id}`}>
                <Check size={14} />
              </button>
              <button onClick={() => { setEditing(false); setValue(service.cost != null ? String(service.cost) : ""); }}
                className="p-1.5 rounded bg-slate-200 text-slate-600 hover:bg-slate-300" data-testid={`cost-cancel-${service.id}`}>
                <XIcon size={14} />
              </button>
            </div>
          ) : (
            <button onClick={() => setEditing(true)}
              className={`font-mono text-right px-2 py-1 rounded hover:bg-slate-100 transition-colors ${cost == null ? "text-amber-600 font-bold" : "text-slate-900"}`}
              data-testid={`cost-value-${service.id}`}>
              {cost != null ? `$${Number(cost).toFixed(2)}` : "Falta configurar"}
            </button>
          )
        ) : (
          <span className="text-amber-700 text-xs font-semibold" title="Clasificá para calcular">Pendiente</span>
        )}
      </td>
      <td className="p-3 text-right font-mono">
        {operativo != null ? <span className="text-slate-900">${operativo.toFixed(2)}</span> : <span className="text-slate-400">—</span>}
      </td>
      <td className="p-3 text-right font-mono">
        {baseEst != null ? <span className="text-emerald-700 font-semibold">${baseEst.toFixed(2)}</span> : <span className="text-slate-400">—</span>}
      </td>
      <td className="p-2 text-right sticky right-0 z-10 bg-white group-hover/row:bg-slate-50 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.15)]" data-testid={`service-actions-${service.id}`}>
        <div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
          <button onClick={onHistory} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Historial de cambios" data-testid={`cost-history-btn-${service.id}`}>
            <HistIcon size={16} />
          </button>
          <button
            onClick={() => onDelete(service)}
            className="p-1.5 rounded hover:bg-red-50 text-red-500"
            title="Eliminar reparación"
            data-testid={`service-delete-${service.id}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function CostHistoryModal({ serviceId, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/admin/services/${serviceId}/cost-history`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [serviceId]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="cost-history-modal">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-heading font-bold">Historial de cambios de costo</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><XIcon size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto">
          {items == null ? (
            <div className="text-center text-slate-500">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-slate-500 py-6">Sin cambios registrados.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((h) => (
                <li key={h.id} className="py-3 flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm">
                      <b className="text-red-600">{h.old_cost != null ? `$${Number(h.old_cost).toFixed(2)}` : "—"}</b>
                      {" → "}
                      <b className="text-emerald-700">{h.new_cost != null ? `$${Number(h.new_cost).toFixed(2)}` : "—"}</b>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono">
                      {h.changed_by_name} · {new Date(h.changed_at).toLocaleString()}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateServiceModal({ categories, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    category_id: "",
    subcategory_id: "",
    price_type: "FIXED",
    price: "",
    price_min: "",
    price_max: "",
    price_from: "",
    parts_policy: "PENDING",
    cost: "",
    visible_to_client: true,
    active: true,
    notes: "",
  });
  const [subs, setSubs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!form.category_id) { setSubs([]); return; }
    api.get(`/subcategories?category_id=${form.category_id}`).then((r) => setSubs(r.data));
  }, [form.category_id]);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    const policy = form.parts_policy;
    const body = {
      name: form.name.trim(),
      category_id: form.category_id || null,
      subcategory_id: form.subcategory_id || null,
      price_type: form.price_type,
      parts_policy: policy,
      visible_to_client: form.visible_to_client,
      active: form.active,
      notes: form.notes || "",
    };
    const pt = form.price_type;
    const toNum = (v) => (v === "" || v == null ? null : Number(v));
    if (pt === "FIXED") body.price = toNum(form.price);
    if (pt === "RANGE") { body.price_min = toNum(form.price_min); body.price_max = toNum(form.price_max); }
    if (pt === "FROM") body.price_from = toNum(form.price_from);
    // cost solo si WITH_PARTS; NO_PARTS fuerza 0; PENDING deja null
    if (policy === "WITH_PARTS") body.cost = toNum(form.cost);
    else if (policy === "NO_PARTS") body.cost = 0;
    else body.cost = null;

    setSaving(true);
    try {
      await api.post("/services", body);
      onCreated();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return createPortal((
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex sm:items-center sm:justify-center sm:p-4"
      onClick={onClose}
      data-testid="create-service-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="create-service-modal"
        className="
          bg-white
          flex flex-col
          w-full h-full
          sm:h-auto sm:rounded-2xl
          sm:w-[min(800px,calc(100vw-32px))]
          sm:max-h-[calc(100vh-40px)]
          shadow-2xl
          overflow-hidden
        "
      >
        {/* Header — siempre visible */}
        <div className="shrink-0 px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0">
              <Plus size={18} />
            </div>
            <div className="min-w-0">
              <h3 className="font-heading font-bold text-slate-900 text-base sm:text-lg truncate">Agregar reparación / servicio</h3>
              <p className="text-xs text-slate-500 hidden sm:block">Catálogo compartido con órdenes y presupuestos</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 shrink-0"
            data-testid="create-service-close"
            aria-label="Cerrar"
          >
            <XIcon size={18} />
          </button>
        </div>

        {/* Body — scroll interno */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* 1. INFORMACIÓN */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">1 · Información del servicio</div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Nombre <span className="text-rose-500">*</span></label>
                <input
                  value={form.name}
                  onChange={(e) => upd("name", e.target.value)}
                  className="input-base w-full"
                  placeholder="Ej: Cambio pantalla LCD TV 32''"
                  data-testid="create-service-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Categoría</label>
                  <select
                    value={form.category_id}
                    onChange={(e) => upd("category_id", e.target.value)}
                    className="input-base w-full"
                    data-testid="create-service-category"
                  >
                    <option value="">—</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Subcategoría / tipo de equipo</label>
                  <select
                    value={form.subcategory_id}
                    onChange={(e) => upd("subcategory_id", e.target.value)}
                    className="input-base w-full"
                    disabled={!form.category_id}
                    data-testid="create-service-subcategory"
                  >
                    <option value="">—</option>
                    {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </section>

          {/* 2. PRECIO */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">2 · Precio</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Tipo de precio</label>
                <select
                  value={form.price_type}
                  onChange={(e) => upd("price_type", e.target.value)}
                  className="input-base w-full"
                  data-testid="create-service-price-type"
                >
                  <option value="FIXED">Fijo</option>
                  <option value="RANGE">Rango</option>
                  <option value="FROM">Desde</option>
                  <option value="VARIABLE">Variable</option>
                  <option value="CONSULT">Consultar</option>
                </select>
              </div>
              {form.price_type === "FIXED" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Precio</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.price}
                    onChange={(e) => upd("price", e.target.value)}
                    className="input-base w-full"
                    data-testid="create-service-price"
                    placeholder="0.00"
                  />
                </div>
              )}
              {form.price_type === "RANGE" && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Mínimo</label>
                    <input type="number" min="0" step="0.01" value={form.price_min} onChange={(e) => upd("price_min", e.target.value)} className="input-base w-full" placeholder="0.00" />
                  </div>
                  <div className="sm:col-start-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Máximo</label>
                    <input type="number" min="0" step="0.01" value={form.price_max} onChange={(e) => upd("price_max", e.target.value)} className="input-base w-full" placeholder="0.00" />
                  </div>
                </>
              )}
              {form.price_type === "FROM" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Desde</label>
                  <input type="number" min="0" step="0.01" value={form.price_from} onChange={(e) => upd("price_from", e.target.value)} className="input-base w-full" placeholder="0.00" />
                </div>
              )}
            </div>
          </section>

          {/* 3. COSTO / CLASIFICACIÓN */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">3 · Costo y clasificación</div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">¿Lleva repuesto?</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <PolicyRadio value="NO_PARTS" current={form.parts_policy} onPick={(v) => upd("parts_policy", v)} label="No lleva repuesto" icon={Minus} />
                  <PolicyRadio value="WITH_PARTS" current={form.parts_policy} onPick={(v) => upd("parts_policy", v)} label="Sí lleva repuesto" icon={Package} />
                  <PolicyRadio value="PENDING" current={form.parts_policy} onPick={(v) => upd("parts_policy", v)} label="Pendiente de definir" icon={HelpCircle} />
                </div>
              </div>
              {form.parts_policy === "WITH_PARTS" && (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Costo del repuesto</label>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.cost}
                    onChange={(e) => upd("cost", e.target.value)}
                    className="input-base w-full sm:max-w-[260px]"
                    placeholder="0.00"
                    data-testid="create-service-cost"
                  />
                </div>
              )}
              {form.parts_policy === "NO_PARTS" && (
                <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  Sin costo de repuesto. Se usará el <strong>operativo imputado</strong> según la lógica financiera del taller.
                </p>
              )}
            </div>
          </section>

          {/* 4. OPCIONES */}
          <section>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">4 · Opciones</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm rounded-xl border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={form.visible_to_client} onChange={(e) => upd("visible_to_client", e.target.checked)} className="w-4 h-4 accent-slate-900" />
                <span>Visible al cliente</span>
              </label>
              <label className="flex items-center gap-2 text-sm rounded-xl border border-slate-200 px-3 py-2.5 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} className="w-4 h-4 accent-slate-900" />
                <span>Activo</span>
              </label>
            </div>
          </section>
        </div>

        {/* Footer — sticky */}
        <div className="shrink-0 px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-heading font-bold text-slate-700 hover:bg-slate-100"
            data-testid="create-service-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="create-service-save"
            className="px-5 py-2.5 rounded-xl text-sm font-heading font-bold bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? "Guardando…" : (<><Check size={14} /> Guardar</>)}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function PolicyRadio({ value, current, onPick, label, icon: Icon }) {
  const active = value === current;
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      data-testid={`policy-radio-${value}`}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

